import Cookies from 'js-cookie'
import dayjsDuration from 'dayjs/plugin/duration'

import dayjs from 'src/Helpers/dayjs'
import { APP } from 'src/Configs/App'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { AlbumImage } from 'src/Types/tracks'

export interface UtmObj {
  referrer_url?: string
  fbp?: string
  fbc?: string
  client_user_agent?: string
  gclid?: string
  ref?: string
}

import { CURRENCY } from './currency'
import {
  DEFAULT_PERIOD_TO_START_CAMPAIGN,
  FIXED_BY,
  gaReferrerBlacklist,
} from './constants'

export const kFormatter = (value: number): string | number => {
  const MIN_NUMBER = 999
  const MAX_NUMBER = 1000000
  const ROUNDING_INDEX = 3
  const SLICE_END_INDEX = -1

  if (value > MIN_NUMBER && value < MAX_NUMBER) {
    return `${
      Math.sign(value) *
      parseFloat(
        (Math.abs(value) / 1000)
          .toFixed(ROUNDING_INDEX)
          .slice(0, SLICE_END_INDEX),
      )
    }k`
  }
  if (value > MAX_NUMBER) {
    return `${
      Math.sign(value) *
      parseFloat(
        (Math.abs(value) / MAX_NUMBER)
          .toFixed(ROUNDING_INDEX)
          .slice(0, SLICE_END_INDEX),
      )
    }m`
  }
  return Math.sign(value) * Math.abs(value)
}

export const currencySymbolConverter = (currency?: string): string => {
  if (!currency) {
    return 'USD'
  }

  if (currency in CURRENCY) {
    return CURRENCY[currency as keyof typeof CURRENCY]
  }

  return currency
}

export const getCookieByName = (value: string): string | undefined => {
  const matches = document.cookie.match(
    new RegExp(
      `(?:^|; )${value.replace(
        // eslint-disable-next-line no-useless-escape
        /([\.$?*|{}\(\)\[\]\\\/\+^])/g,
        '\\$1',
      )}=([^;]*)`,
    ),
  )

  // eslint-disable-next-line no-undefined
  return matches ? decodeURIComponent(matches[1]) : undefined
}

export const checkReferrerUrl = (url: string): boolean => {
  let res = true

  for (const element of gaReferrerBlacklist) {
    if (url.includes(element)) {
      res = false
    }
  }

  return res
}

export const getUtmParameters = (value?: string): UtmObj => {
  const obj: { [key: string]: string } = {}
  const fbp = getCookieByName('_fbp')
  const fbc = getCookieByName('_fbc')
  const { userAgent } = window.navigator
  const isReferrerUrlValid = checkReferrerUrl(document.referrer)

  if (document.referrer && isReferrerUrlValid) {
    obj.referrer_url = document.referrer
  }

  if (fbp) {
    obj.fbp = fbp
  }
  if (fbc) {
    obj.fbc = fbc
  }
  if (userAgent) {
    obj.client_user_agent = userAgent
  }

  if (typeof value === 'string') {
    const slicedAndSplitValues = value.slice(1).split('&')

    for (const element of slicedAndSplitValues) {
      const splitElement = element.split('=')
      obj[splitElement[0]] = decodeURIComponent(splitElement[1])
    }
  }

  return obj
}

export const numberFormatSpaces = (number: number | undefined): string | 0 =>
  number ? number.toLocaleString('fr', { maximumFractionDigits: 2 }) : 0

export const numberFormatCommas = (
  number: number | undefined | null,
): string | 0 =>
  number ? number.toLocaleString('en-US', { maximumFractionDigits: 2 }) : 0

export const hidePartOfEmailWithStars = (arg0: string): string => {
  if (!arg0) {
    return ''
  }
  const ROUNDING_INDEX = 0.3
  const percentageOfString = Math.round(arg0.length * ROUNDING_INDEX)
  const firstLetterOfEmail = arg0.slice(0, percentageOfString)
  const hiddenPartOfEmail = arg0
    .substring(0, arg0.indexOf('@') - percentageOfString)
    .replace(/./gi, '*')
  const secondPartOfEmail = arg0.substring(arg0.indexOf('@') + 1)
  return `${firstLetterOfEmail}${hiddenPartOfEmail}@${secondPartOfEmail}`
}

export const removeCookiesHandler = (): void => {
  Cookies.remove('_ga')
  Cookies.remove(`${APP.GA_CONTAINER}`)
  Cookies.remove('sondcsrd')
  Cookies.remove('soundCampaignUtm')
  Cookies.remove('_fbp')
  Cookies.remove('_fbc')
}

type CampaignDateValue = string | Date | null

export const getIsMoreThenDaysLeft = (
  futureDate?: CampaignDateValue,
): boolean => {
  const periodToStartCampaignInHours = DEFAULT_PERIOD_TO_START_CAMPAIGN * 24

  if (!futureDate) {
    return false
  }
  const date = dayjs(futureDate)
  const now = dayjs()

  const diffInHours = date.diff(now, 'hour')
  return diffInHours > periodToStartCampaignInHours
}

export const handleDefinePlatform = (params: {
  isSpotifyTrack: boolean
  isTikTokTrack: boolean
}): CampaignPlatformTypeEnum => {
  if (params.isSpotifyTrack && params.isTikTokTrack) {
    return CampaignPlatformTypeEnum.SPOTIFY_TIKTOK
  }
  if (!params.isSpotifyTrack && params.isTikTokTrack) {
    return CampaignPlatformTypeEnum.TIKTOK
  }
  return CampaignPlatformTypeEnum.SPOTIFY
}

export const convertTimeHandler = (params: {
  value: number
  type: 'ms' | 's'
}): string => {
  dayjs.extend(dayjsDuration)
  const duration = dayjs
    .duration({
      milliseconds: params.type === 'ms' ? params.value : 0,
      seconds: params.type === 's' ? params.value : 0,
    })
    .asMilliseconds()

  if (duration < 0) {
    return '00:00'
  }

  const minutes = new Intl.NumberFormat('en-IN', {
    minimumIntegerDigits: 2,
  }).format(dayjs(duration).get('minutes'))

  const seconds = new Intl.NumberFormat('en-IN', {
    minimumIntegerDigits: 2,
  }).format(dayjs(duration).get('seconds'))

  return `${minutes}:${seconds}`
}

export const handleTakePlatformFromUrl = (
  url: string,
): CampaignPlatformTypeEnum | string => {
  if (url.includes('scheduleTikTok')) {
    return CampaignPlatformTypeEnum.TIKTOK
  }
  if (url.includes('schedule')) {
    return CampaignPlatformTypeEnum.SPOTIFY
  }
  return ''
}

export const toReversed = <T>(arr: T[]): T[] => [...arr].reverse()

export const getDefaultStartDate = (isToday?: boolean): string =>
  dayjs()
    .add(isToday ? 0 : DEFAULT_PERIOD_TO_START_CAMPAIGN, 'days')
    .toISOString()

export const getValidatedStartDate = (
  startDate?: CampaignDateValue,
  hasPlan?: boolean,
): string =>
  getIsMoreThenDaysLeft(startDate)
    ? dayjs(startDate).toISOString()
    : getDefaultStartDate(hasPlan)

export const getIsStartDateOutdated = (
  startDate?: CampaignDateValue,
  hasPlan?: boolean,
): boolean => {
  if (!startDate) {
    return false
  }

  const earliestStartDate = hasPlan
    ? dayjs()
    : dayjs().add(DEFAULT_PERIOD_TO_START_CAMPAIGN, 'day')

  return dayjs(startDate).isBefore(earliestStartDate, 'day')
}

export const getValueOrEmptyString = (value?: string): string => value || ''
export const getValueOrZero = (value?: number): number => value || 0
export const getIsTikTokTrackUrl = (value?: string): boolean => {
  if (!value) {
    return false
  }

  return (
    value.startsWith('https://vm.tiktok.com/') ||
    value.startsWith('https://www.tiktok.com/music/') ||
    value.startsWith('https://vt.tiktok.com/') ||
    value.startsWith('https://www.tiktok.com/t/')
  )
}

export const getSpotifyTrackId = (url?: string): string => {
  if (!url) {
    return ''
  }
  const urlParts = url.split('/')
  return urlParts[urlParts.length - 1]
}

const FIRST_SORT_PRIORITY = -1
const SECOND_SORT_PRIORITY = 1

export const filterAndSortByKey = <T, K extends keyof T>(
  items: T[],
  search: string,
  key: K,
): T[] => {
  const searchValue = (search || '').toString().trim().toLowerCase()

  if (!searchValue) {
    return items
  }

  const getValue = (item: T): string =>
    (item?.[key] as string | undefined)?.toLowerCase() || ''

  const exactMatchPriority = (a: string, b: string): number => {
    if (a === searchValue) {
      return FIRST_SORT_PRIORITY
    }
    if (b === searchValue) {
      return SECOND_SORT_PRIORITY
    }
    return 0
  }

  const startsWithPriority = (a: string, b: string): number => {
    if (a.startsWith(searchValue)) {
      return FIRST_SORT_PRIORITY
    }
    if (b.startsWith(searchValue)) {
      return SECOND_SORT_PRIORITY
    }
    return 0
  }

  const containsPriority = (a: string, b: string): number => {
    const aIncludes = a.includes(searchValue)
    const bIncludes = b.includes(searchValue)

    if (aIncludes && !bIncludes) {
      return FIRST_SORT_PRIORITY
    }
    if (!aIncludes && bIncludes) {
      return SECOND_SORT_PRIORITY
    }
    return 0
  }

  const sortFn = (a: T, b: T): number => {
    const aVal = getValue(a)
    const bVal = getValue(b)

    return (
      exactMatchPriority(aVal, bVal) ||
      startsWithPriority(aVal, bVal) ||
      containsPriority(aVal, bVal)
    )
  }

  return items
    .filter((item) => getValue(item).includes(searchValue))
    .sort(sortFn)
}

export const filterAndSortGenres = <
  T extends { genre_name?: string; genreName?: string },
>(
  genres: T[],
  search: string,
  genreKey: 'genre_name' | 'genreName',
): T[] => filterAndSortByKey(genres, search, genreKey)

export const filterAndSortArtists = <T extends { name?: string }>(
  artists: T[],
  search: string,
): T[] => filterAndSortByKey(artists, search, 'name')

export const numberToMoney = (value?: number): string =>
  value ? value.toFixed(FIXED_BY) : '0.00'

export const numberToDollarFormat = (value?: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value ?? 0)

export const getMediumImageUrl = (images?: AlbumImage[]): string => {
  if (!images || images.length < 2) {
    return images?.[0]?.url || ''
  }

  const sorted = [...images].sort((a, b) => (a.width || 0) - (b.width || 0))
  return sorted[1]?.url || sorted[0].url || ''
}
